import {
  AutoClockOutSource,
  OvertimeEntryRecord,
  OvertimeEntryStatus,
  OvertimeEntryStatusLabels,
  OvertimeSettlementOutcome,
  OvertimeThresholdKind
} from '@navio/nsdk';
import { decimalHoursBetween } from 'noddi-util';

// Shared display helpers for overtime entries — used by both the list table and
// the detail sheet so the two stay visually in sync.

// Why the machine closed a timer — shown alongside the "Auto-closed" status.
export const AUTO_CLOCK_OUT_REASON: Record<AutoClockOutSource, string> = {
  [AutoClockOutSource.DEPARTMENT_TIME]: 'department closing time',
  [AutoClockOutSource.DAILY_LIMIT]: 'daily overtime limit',
  [AutoClockOutSource.WEEKLY_LIMIT]: 'weekly overtime limit',
  [AutoClockOutSource.ROUTE_START]: 'a route started'
};

// A supplement percent without trailing zeros: 40 -> "40", 12.5 -> "12.5".
export const formatSupplementPercent = (percent: number): string => Number(percent).toString();

// Short label for a chosen settlement outcome (the approved overtime card / list).
export const overtimeOutcomeLabel: Record<OvertimeSettlementOutcome, string> = {
  [OvertimeSettlementOutcome.PAID_REGULAR]: 'Paid at regular rate',
  [OvertimeSettlementOutcome.PAID_OVERTIME]: 'Paid with supplement',
  [OvertimeSettlementOutcome.TIME_OFF]: 'Time off in lieu'
};

// Decimal hours -> "9h 00m"; round-up at 60m rolls into the hour.
export const formatHoursMinutes = (decimalHours: number): string => {
  const totalMinutes = Math.round(decimalHours * 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours}h ${String(minutes).padStart(2, '0')}m`;
};

// Shift split for one overtime entry: overtime is the OT span; regular is the
// rest of the shift (full span - overtime). Regular is null for an open timer.
export const shiftBreakdown = (entry: OvertimeEntryRecord) => {
  const overtime = decimalHoursBetween(entry.startsAt, entry.endsAt);
  const total = entry.clockOutAt ? decimalHoursBetween(entry.clockInAt, entry.clockOutAt) : null;
  const regular = total === null ? null : Math.max(0, total - overtime);
  return { overtime, regular };
};

// Which threshold made this overtime — the manager's core "why" before deciding.
export const overtimeWhy = (entry: OvertimeEntryRecord): string | null => {
  switch (entry.source) {
    case OvertimeThresholdKind.DAILY_LIMIT:
      return 'Over daily limit';
    case OvertimeThresholdKind.WEEKLY_LIMIT:
      return 'Over weekly limit';
    default:
      return null;
  }
};

// Approving is refused while the worker has a pending correction request in
// the entry's week: approving that request can move this split (backend ADR 0010).
export const PENDING_CORRECTION_REQUEST_LABEL = 'Correction pending';
export const PENDING_CORRECTION_REQUEST_HINT =
  'The worker has a pending correction request this week that can change this overtime. Decide it before approving.';

export const overtimeStatusVariant = (status: OvertimeEntryStatus) => {
  switch (status) {
    case OvertimeEntryStatus.APPROVED:
      return 'success' as const;
    case OvertimeEntryStatus.PENDING:
      return 'warning' as const;
    case OvertimeEntryStatus.REJECTED:
      return 'destructive' as const;
    default:
      return 'outline' as const;
  }
};

// Labels are keyed by enum key (APPROVED), but the record carries the value ("approved") — map value -> label.
export const overtimeStatusLabel: Record<OvertimeEntryStatus, string> = {
  [OvertimeEntryStatus.APPROVED]: OvertimeEntryStatusLabels.APPROVED,
  [OvertimeEntryStatus.PENDING]: OvertimeEntryStatusLabels.PENDING,
  [OvertimeEntryStatus.REJECTED]: OvertimeEntryStatusLabels.REJECTED,
  [OvertimeEntryStatus.WITHDRAWN]: OvertimeEntryStatusLabels.WITHDRAWN
};
